import {
  CheckItemsManager
} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/check-items-manager';
import {Step} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/step';
import {DiagnosticError, MediaConnectionBitrateTest} from '@twilio/rtc-diagnostics';
import {
  SPEED_TEST_STATUS,
  TestPassState,
  TestResponse,
  TestResponseCommands
} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/models';
import {Observable} from 'rxjs';
import {AccessType, Role} from '@app/modules/shared/data/constants';
import {WrtcService} from '@app/modules/wrtc/services/wrtc.service';

export class NetworkCheckManager extends CheckItemsManager {
  icon = 'networkSpeed-g';
  name = 'Network speed';

  constructor(userRole: Role, private wrtcService: WrtcService) {
    super(userRole);
    this.steps.push(new ConnectionQuality(wrtcService));
  }
}

export class ConnectionQuality extends Step {
  name = 'Connection quality';
  title = 'Checking speed...';

  averageBitrate: number;

  response=false;

  constructor(private wrtcService: WrtcService) {
    super();
  }
  doRunStep() {
    return new Observable<TestResponse>(subscriber => {
      // eslint-disable-next-line max-len
      const errorMessage='Your Internet speed is slow, which may cause video lag. If you can\'t improve it right now, click Skip. You may need to upgrade your network for future class classes. If you seek additional help, reach out to tech support using the chat bubble at the bottom or contact the office.';
      setTimeout(()=>{
        if (!this.response){
          this.handleError(errorMessage);
          subscriber.next(new TestResponse(TestResponseCommands.SHOW_ERROR));
        }
      },25000);
      this.wrtcService.getIceServersUrls()
        .subscribe((response) => {
          this.response=true;
          const iceServers = response;
          const iceServersForTest = [];
          iceServersForTest.push({urls: iceServers.find(item => item.urls.includes('stun:global.stun.twilio.com')).urls});
          const {urls, username, credential} = iceServers
            .find(item => item.url === 'turn:global.turn.twilio.com:3478?transport=udp');
          iceServersForTest.push({urls, username, credential});
          iceServersForTest.forEach(iceServer => {
            iceServer.urls = iceServer.urls.replace('global', 'ashburn');
          });

          // // Use the TURN credentials using the iceServers parameter
          const mediaConnectionBitrateTest: MediaConnectionBitrateTest = WrtcService.testMediaConnectionBitrate(iceServers,
            (bitrate: number) => {},
            (error: DiagnosticError) => {
              this.handleError(errorMessage);
              subscriber.next(new TestResponse(TestResponseCommands.SHOW_ERROR));
            },
            (report: MediaConnectionBitrateTest.Report) => {
              this.testResult.status = TestPassState.PASSED;
              this.averageBitrate = report.averageBitrate;
              this.testResult.text = this.getTestStatusMessage(report.averageBitrate);
              this.testStatus=this.testResult.text;
              if (this.testResult.text === SPEED_TEST_STATUS.POOR || !window.navigator.onLine) {
                this.handleError(errorMessage);
                subscriber.next(new TestResponse(TestResponseCommands.SHOW_ERROR));
              } else {
                subscriber.next(new TestResponse(TestResponseCommands.NEXT));
              }
            }
          );
          setTimeout(() => {
            mediaConnectionBitrateTest.stop();
          }, 10000);
        });
    });
  }

  getTestStatusMessage(averageBitrate: any) {
   if (averageBitrate > 10000) {
      return SPEED_TEST_STATUS.EXCELLENT;
    }
    if (averageBitrate > 5000) {
      return SPEED_TEST_STATUS.GOOD;
    }
    if (averageBitrate > 2000) {
      return SPEED_TEST_STATUS.SUBOPTIMAL;
    }
    return SPEED_TEST_STATUS.POOR;
  }

  override toString(): string {
    let stepMessage;

    switch (this.testResult.status) {
      case TestPassState.PENDING: {
        stepMessage = 'not accomplished.';
        break;
      }
      case TestPassState.PASSED: {
        stepMessage = this.testResult.text + '(' + Math.floor(this.averageBitrate) + ' kBits/sec.)';
        break;
      }
      case TestPassState.FAILED: {
        stepMessage = this.testResult.text + '(' + Math.floor(this.averageBitrate) + ' kBits/sec.)';
        break;
      }
    }
    return `${this.title} -- ${stepMessage}, `;
  }
  handleError(errorMessage: string){
    this.testResult.errorMassage = errorMessage;
    this.testResult.status = TestPassState.FAILED;
    this.testResult.text = SPEED_TEST_STATUS.POOR;
    this.testStatus = SPEED_TEST_STATUS.POOR;
  }
}
